import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import {
  fetchMeetingMinutesTemplates,
  deleteMeetingMinutesTemplate,
} from "../../../../api/meetingMinutes";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";

export const MeetingMinutesTemplatesList = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
      };
      const response = await fetchMeetingMinutesTemplates(params);
      setTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError(true);
      toast.error(t("meeting_minutes.template.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("meeting_minutes.template.delete_confirm"))) {
      return;
    }

    try {
      await deleteMeetingMinutesTemplate(id);
      toast.success(t("meeting_minutes.template.deleted"));
      fetchData();
    } catch (err) {
      console.error("Error deleting template:", err);
      toast.error(t("meeting_minutes.template.delete_error"));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  if (error) {
    return <ResourceError />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={t("meeting_minutes.templates.list")}
        mainButton={{
          label: t("meeting_minutes.templates.new"),
          path: "/dashboard/meeting-minutes/templates/new",
          icon: PlusIcon,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("meeting_minutes.templates.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {templates.length === 0 ? (
          <ResourceEmpty message={t("meeting_minutes.templates.no_results")} />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {templates.map((template) => (
                <li key={template.id}>
                  <div className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-6 w-6 text-gray-400 mr-3" />
                      <div>
                        <Link
                          to={`/dashboard/meeting-minutes/templates/${template.id}/edit`}
                          className="text-lg font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          {template.title}
                        </Link>
                        <p className="text-sm text-gray-500 mt-1">
                          {template.meeting_type}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/dashboard/meeting-minutes/templates/${template.id}/edit`}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

